import { DateTime } from 'luxon';
import { Observable, of } from 'rxjs';
import { PaymentsAccount, Address, UsState, InstitutionId, MOCK_ADDRESS } from './account.model';
import { Beneficiary, BeneficiaryBank, Intermediary, MOCK_BENEFICIARY, MOCK_BENEFICIARY_BANK, Template, PaymentType } from './template.model';
import {
	PaymentStatus,
	ApprovalUser,
	StageRuleDetail,
	MOCK_PAYMENT_EXECUTION_STAGE,
	WorkflowStageRuleCondition,
	WorkflowStageRuleVariable,
} from './workflow.model';
import { CountryCode } from '@trovata/app/shared/utils/country-data';
import { FlagIconSize, getFlagIconURLByCode } from '@trovata/app/shared/utils/country-flag-svg-helper';
import { SortDirection } from '@angular/material/sort';

export type PaymentRecord = Payment | BatchPayment;

/**
 * Type guard to check if a PaymentRecord is a Payment.
 *
 * @param record - The payment record to check
 * @returns True if the record is a Payment, false if it's a BatchPayment
 *
 * @example
 * if (isPayment(record)) {
 *   // TypeScript knows record is Payment here
 * }
 */
export function isPayment(record: PaymentRecord): record is Payment {
	return 'paymentId' in record;
}

/**
 * Type guard to check if a PaymentRecord is a BatchPayment.
 *
 * @param record - The payment record to check
 * @returns True if the record is a BatchPayment, false if it's a Payment
 *
 * @example
 * if (isBatchPayment(record)) {
 *   // TypeScript knows record is BatchPayment here
 * }
 */
export function isBatchPayment(record: PaymentRecord): record is BatchPayment {
	return 'batchId' in record && 'stats' in record;
}

/**
 * Utility function to extract the unique ID from either Payment or BatchPayment.
 *
 * @param record - The payment record
 * @returns The paymentId for Payment or batchId for BatchPayment
 * @throws Error if record is null/undefined
 *
 * @example
 * const id = getPaymentRecordId(record);
 */
export function getPaymentRecordId(record: PaymentRecord): string {
	if (!record) {
		throw new Error('getPaymentRecordId: record is null or undefined');
	}
	return isPayment(record) ? record.paymentId : record.batchId;
}

export interface GetPaymentRecordsResponse {
	payments: PaymentRecord[];
	totalCount: number;
}

export interface PaymentsListQuery {
	from?: number;
	size?: number;
	initiated?: string;
	sortKey?: string;
	sortDir?: 'asc' | 'desc';
	types?: PaymentType[];
	statuses?: PaymentStatus[];
	search?: string;
	needsReview?: boolean;
}

export enum PaymentsTab {
	allPayments = 'allPayments',
	readyForReview = 'readyForReview',
	openPayments = 'openPayments',
}

export const PAYMENTS_PAGINATION: Readonly<{
	DEFAULT_PAGE_SIZE: number;
}> = {
	DEFAULT_PAGE_SIZE: 100,
} as const;

export interface PaymentsListFilters {
	from?: number;
	size?: number;
	paymentId?: string[];
	excludeAccountId?: string[];
	batchId?: string[] | string;
	assigned?: boolean;
	initiated?: string;
	sortKey?: string;
	sortDir?: SortDirection;
	types?: PaymentType[];
	statuses?: PaymentStatus[];
	search?: string;
}

export interface AccountsListBody {
	from?: number;
	size?: number;
	includeBalance?: boolean;
	accountId?: string[];
	excludeAccountId?: string[];
}

export interface Payment {
	account: PaymentDetailAccount;
	amount: number;
	confirmationId: string;
	developerInitiated: boolean;
	history: PaymentHistory[];
	initiated: string;
	initiator: string | null;
	institution: InstitutionId;
	instructions: PaymentInstructions;
	name: string;
	paymentId: string;
	remittance: string;
	status: PaymentStatus;
	source: PaymentSource;
	submission: PaymentSubmission;
	template: PaymentTemplate | null;
	type: PaymentType;
	valueDate: string | null;
	needsReview: boolean;

	settledCurrency?: string;
	settledAmount?: number;
	currency?: string;
	iban?: string;
	bic?: string;
	isActionRequired?: boolean;
	execution?: PaymentExecution;
	customerId?: string;
	executionId?: string;
	submit?: boolean;
	batchId?: string;
	batch?: PaymentsBatchInfo;
	attachments?: PaymentAttachment[];
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	additionalInformation: { [key: string]: any };
}

export interface PaymentAttachment {
	attachmentId: string;
	fileName: string;
	fileType: string;
	link: string;
}

export interface PaymentsBatchInfo {
	batchId: string;
	name: string;
	status: PaymentStatus;
}

export interface PaymentTemplate {
	templateId: string;
	name: string;
}

export interface PaymentSubmission {
	completed: boolean;
	completedTimestamp: null;
	confirmationId: string;
	endToEnd: string;
	institutionBatchId: string;
	institutionPaymentId: string;
	institutionSecondaryPaymentId: string;
	institutionStatus: string;
	submitted: boolean;
	submittedTimeStamp: null;
}

export interface PaymentDetailAccount {
	accountId: string;
	accountNumber?: string;
	achCompanyId: string;
	address: Address;
	companyName: string;
	institutionId: InstitutionId;
	name: string;
	routingNumber?: string;
	workflowId: string;
	bic?: string;
	iban?: string;
	currency: string;
	country: string;
}

export interface BatchPaymentDetailAccount {
	accountId: string;
	accountNumber: string;
	achCompanyId: string;
	achEnabled: boolean;
	address: Address;
	companyName: string;
	currency: string;
	customerId: string;
	institutionId: InstitutionId;
	name: string;
	routingNumber: string;
	rtpEnabled: boolean;
	bic?: string;
	iban?: string;
	transferEnabled: boolean;
	wireEnabled: boolean;
	withdrawalEnabled: boolean;
	workflow: BatchPaymentWorkflow;
	workflowId: string;
}

export interface BatchPaymentWorkflow {
	workflowId: string;
	name: string;
}

export interface PaymentExecution {
	name: string;
	stages: PaymentExecutionStage[];

	workflowId?: string;
}

export interface PaymentExecutionStage {
	approval: PaymentExecutionStageApproval;
	name: string;
	rules: PaymentExecutionStageRule[];
	signatures: PaymentExecutionStageSignature[];
	status: PaymentStatus;
	stageId: string;
	needsReview: boolean;

	needAuthUserApproval?: boolean;
	stageRuleDetails?: StageRuleDetail[];
	uiIndex?: number;
}

export interface PaymentExecutionStageApproval {
	numApprovals: number;
	userIds: string[];
	users: ApprovalUser[];
}

export interface PaymentExecutionStageRule {
	approval: PaymentExecutionStageRuleApproval;
	condition: WorkflowStageRuleCondition;
	numericConstant: number;
	variable: WorkflowStageRuleVariable;

	required?: boolean;
}

export interface PaymentExecutionStageRuleApproval {
	numApprovals: number;
	userIds: string[];

	users?: ApprovalUser[];
}

export interface PaymentExecutionStageSignature {
	comment: string;
	email: string;
	familyName: string;
	givenName: string;
	timestamp: string;
	userId: string;
}

export interface PaymentInstructions {
	beneficiary: Beneficiary;
	beneficiaryBank: BeneficiaryBank;
	intermediaryBank: Intermediary;
	source: PaymentInstructionsSource;
}

export interface BatchPaymentInstructions {
	beneficiary: BatchPaymentInstructionsBeneficiary;
	beneficiaryBank: BatchPaymentInstructionsBeneficiaryBank;
}

export interface BatchPaymentInstructionsBeneficiary {
	accountNumber: string;
	name: string;
	iban?: string;
	address?: Address;
}

export interface BatchPaymentInstructionsBeneficiaryBank {
	routingNumber: string;
	bic?: string;
	address?: Address;
}

export interface PaymentInstructionsSource {
	name: string;
	type: string;
}
export interface PaymentHistory {
	comment: string;
	description: string;
	metadata: PaymentHistoryMetaData;
	timestamp: string;
}

export interface PaymentHistoryMetaData {
	userId: string;
	mfaVerified?: boolean;
}

export interface PaymentToCreate {
	name: string;
	remittance: string;
	type: PaymentType;
	templateId: string;
	accountId: string;
	currency: string;
	country: string;
	beneficiaryAccountId: string;
	instructions: PaymentToCreateInstructions;
	amount: number;
	valueDate: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	additionalInformation: { [key: string]: any };
	beneficiary?: WithdrawalBeneficiary;
}

export interface WithdrawalBeneficiary {
	destinationType: string;
	destinationId: string;
	displayName: string;
}

export interface InitialTransferPayment extends PaymentToCreate {
	beneficiaryAccountId: string;
}

export interface InitialTemplatePayment extends PaymentToCreate {
	templateId: string;
}

export interface InitialManualPayment extends PaymentToCreate {
	instructions: PaymentToCreateInstructions;
}

export interface PaymentToCreateInstructions {
	beneficiary: BeneficiaryDetails;
	beneficiaryBank: BeneficiaryBankDetails;
	intermediaryBank: IntermediaryBankDetails;
}

export interface BeneficiaryDetails {
	name: string;
	accountNumber?: string;
	iban?: string;
	address: Address;
}

export interface BeneficiaryBankDetails {
	name: string;
	country: string;
	routingNumber?: string;
	bic?: string;
	address: Address;
}

export interface IntermediaryBankDetails {
	name: string;
	country: string;
	routingNumber?: string;
	bic?: string;
	accountNumber?: string;
	iban?: string;
	address: Address;
}

export interface AccountInfoFormValues {
	account: PaymentsAccount;
	amount: number;
}

export interface TemplateInfoFormValues {
	manual: boolean;
	template: Template;
}

export interface BeneficiaryFormValues {
	saveAsTemplate: boolean;
	manualTemplateName: string;
	name: string;
	accountNumber: string;
	routingNumber: string;
	type: string;
	street: string;
	streetLineTwo: string;
	city: string;
	state: UsState;
	zip: string;
	country: CountryCode;
	bankName: string;
	bankStreet: string;
	bankStreetLineTwo: string;
	bankCity: string;
	bankState: UsState;
	bankZip: string;
	bankCountry: CountryCode;
}

export interface TransferAccountFormValues {
	account: PaymentsAccount;
}

export interface Signature {
	email: string;
	familyName: string;
	givenName: string;
	timestamp: string;
	userId: string;
}

export enum StageFeedbackStatus {
	APPROVE = 'approve',
	CHANGE = 'change',
	REJECT = 'reject',
}

export interface StageFeedback {
	stageId: string;
	type: StageFeedbackStatus;

	paymentId?: string;
	batchId?: string;
	comment?: string;
	refreshToken?: string;
	otp?: string;
}

export interface PaymentSubmissionOrCancelation {
	paymentId?: string;
	batchId?: string;
}

export interface StageFeedbackFormValues {
	stage: PaymentExecutionStage;
	comment: string;
	feedbackType: string;
}

// ============================================
// Bulk Approval Request/Response Types
// ============================================

/**
 * Represents a single item in a bulk approval request.
 * Must have exactly one of paymentId or batchId (XOR constraint).
 */
export interface BulkApprovalRequestItem {
	paymentId?: string;
	batchId?: string;
	stageId: string;
	type: 'approve' | 'reject';
	comment?: string;
}

/**
 * Request body for POST /v1/payments/approval.
 * OTP and refreshToken are optional, required only for MFA-enabled release approvals.
 */
export interface BulkApprovalRequest {
	readonly approvals: readonly BulkApprovalRequestItem[];
	readonly otp?: string;
	readonly refreshToken?: string;
}

/**
 * Error details for a failed approval item.
 */
export interface BulkApprovalError {
	paymentId: string | null;
	batchId: string | null;
	stageId: string | null;
	error: {
		message: string;
		status: number;
	};
}

/**
 * Full response from POST /v2/payments/approval.
 * Note: HTTP status is always 200, check errors array for failures.
 */
export interface BulkApprovalResponse {
	payments: PaymentRecord[];
	errors: BulkApprovalError[];
}

/**
 * Result of bulk approval operation for component consumption.
 * Provides both raw API response and convenience categorization.
 */
export interface BulkApprovalResult {
	response: BulkApprovalResponse;
	successfulPayments: Payment[];
	successfulBatches: BatchPayment[];
	failedItems: BulkApprovalError[];
	hasPartialSuccess: boolean;
	allSucceeded: boolean;
	allFailed: boolean;
}

export enum MarketingVideoPageVideoSrc {
	paymentsPage = '//play.vidyard.com/uVYFykbyREqp5biQ3KfMHm.html?',
	paymentPage = '//play.vidyard.com/uVYFykbyREqp5biQ3KfMHm.html?',
	accountsPage = '//play.vidyard.com/nfv1SbPwvQDYXEKi716m1q.html?',
	createEditWorkflowPage = '//play.vidyard.com/WgusL1h3CCSRtnyXqE2JmM.html?',
	payPage = '//play.vidyard.com/Xybhv3JqNJiNbLnWtT19x8.html?',
	templatesPage = '//play.vidyard.com/A9xEPawsPypwAhvpE5viKM.html?',
	usersPage = '//play.vidyard.com/3ocq2VssC1iq4DHGMKLEHR.html?',
	workflowsPage = '//play.vidyard.com/WgusL1h3CCSRtnyXqE2JmM.html?',
}

export interface ValidateBatchCsvFileResponse {
	columns?: ValidateBatchCsvFileResponseColumn[];
	payments?: BatchsPayment[];
	errors?: BatchLevelError[];
}

export interface LexisNexisBankInformationResponse {
	bankAddress: BankAddress;
	bankIdentifier: string;
	bankIdentifierType: string;
	bankName: string;
	bic?: string;
	countryCode: CountryCode;
	retrievedAt: string;
	rountingNumber?: string;
	sortCode: string;
	status: string;
}

export interface InstitutionCountry {
	identifier: string;
	institution: string;
	paymentType: PaymentType;
	country: string;
	countryCode: CountryCode;
	bicAllowed: boolean;
	bicRequired: boolean;
	routingAllowed: boolean;
	routingRequired: boolean;
	routingTypes: string[];
	flagUrl?: string;
}

export interface InstitutionAddtlInstruction {
	identifier: string;
	institution: string;
	country: string;
	paymentType: PaymentType;
	fieldDataType: InstitutionAddtlInstructionDataType;
	required: boolean;
	fieldUIType: InstitutionAddtlInstructionUIType;
	fieldOptions: Object[];
	fieldOptionsDisplay?: InstitutionAddtlInstructionOptDisplay[];
	fieldOrder: number;
	fieldTitle: string;
	fieldDescription: string;
	field: string;
}

export interface InstitutionAddtlInstructionOptDisplay {
	value: string;
	display: string;
}

export enum InstitutionAddtlInstructionDataType {
	text = 'text',
}

export enum InstitutionAddtlInstructionUIType {
	text = 'text',
	list = 'list',
}

export interface BankAddress {
	branch: string;
	city: string;
	country: string;
	state: string;
	street: string;
	streetLineTwo: string;
	zip: string;
}

export interface BatchLevelError {
	message: string;
	name: string;
	status: number;
}

export interface BatchsPaymentLevelError {
	fieldName: string;
	message: string;
	name: string;
	status: number;
}

export interface ValidateBatchCsvFileResponseColumn {
	fieldName: string;
	label: string;
}

export interface GetBatchPaymentsResponse {
	batches: BatchPayment[];
	totalCount: number;
}

export interface GetPaymentsV1Response {
	payments: Payment[];
	totalCount: number;
}

export interface BatchPayment {
	batchId: string;
	name: string;
	status: PaymentStatus;
	amount: number;
	developerInitiated: boolean;
	initiated: string;
	valueDate: string | null;
	initiator: string;
	releaser: string;
	execution: PaymentExecution;
	history: PaymentHistory[];
	stats: BatchPaymentStats;
	submission: BatchPaymentSubmission;
	source: PaymentSource;
	needsReview: boolean;

	childPayments?: Payment[];
	currency?: string;
	paymentType?: PaymentType;
	remittance?: string;
	// international fields below (currently unused)
	settledCurrency?: string;
	settledAmount?: number;
	confirmationId?: string;
}

export interface BatchPaymentStats {
	completedAmount: number;
	openAmount: number;
	completedPayments: number;
	openPayments: number;
	rejectedPayments: number;
	totalPayments: number;
}

export interface BatchPaymentSubmission {
	completed: boolean;
	completedTimestamp: string;
	submitted: boolean;
	submittedTimestamp: string;
}

export enum ActiveWorkflowStage {
	review = 'review',
	approve = 'approve',
	release = 'release',
}

/**
 * Maps a workflow stage name to its corresponding ActiveWorkflowStage enum value.
 *
 * Workflow progression:
 * - Preparer/Reviewer stages → ActiveWorkflowStage.review (user reviews the payment)
 * - Approver stage → ActiveWorkflowStage.approve (user approves the payment)
 * - Releaser stage → ActiveWorkflowStage.release (user releases the payment for execution)
 *
 * @param stageName - The name of the workflow stage (e.g., 'Preparer', 'Reviewer', 'Approver', 'Releaser')
 * @returns The corresponding ActiveWorkflowStage enum value, or null if the stage name is not recognized
 */
export function getWorkflowStageFromUserType(stageName: string): ActiveWorkflowStage | null {
	switch (stageName) {
		case 'Preparer':
		case 'Reviewer':
			return ActiveWorkflowStage.review;
		case 'Approver':
			return ActiveWorkflowStage.approve;
		case 'Releaser':
			return ActiveWorkflowStage.release;
		default:
			return null;
	}
}

export enum PaymentSource {
	webManual = 'web_manual',
	developerApi = 'developer_api',
	sftpUpload = 'sftp_upload',
}

export interface BatchsPayment {
	name: string;
	amount: number;
	developerInitiated: boolean;
	initiated: string;
	initiator: string;
	institution: InstitutionId;
	remittance: string;
	status: PaymentStatus;
	type: PaymentType;
	valueDate: string | null;
	account: BatchPaymentDetailAccount;
	execution: PaymentExecution | null;
	history: PaymentHistory[] | null;
	instructions: BatchPaymentInstructions;
	submission: PaymentSubmission | null;
	template: PaymentTemplate | null;

	errors?: BatchsPaymentLevelError[];
}

export interface PaymentStats {
	status: PaymentStatsStatus;
	type: PaymentStatsType;
	breakdown: [PaymentStatsDay];
}

export interface PaymentStatsStatus {
	open: number;
	approved: number;
	submitted: number;
	completed: number;
	canceled: number;
	rejected: number;
	rejectedByBank: number;
}

export interface PaymentStatsType {
	rtp: number;
	wire: number;
	ach: number;
	transfer: number;
	withdrawal: number;
}

export interface PaymentStatsDay {
	date: string;
	total: number;
}

export interface AllowedPaymentType {
	paymentType: PaymentType;
	label: string;

	checked?: boolean;
	disabled?: boolean;
}

export interface InternationalWireCountry {
	name: string;
	countryCode: string;
	flagUrl: string;
}

export const internationalWireCountries: InternationalWireCountry[] = [
	{
		name: 'Albania',
		countryCode: CountryCode.AL,
		flagUrl: getFlagIconURLByCode(CountryCode.AL, FlagIconSize.fourByThree),
	},
	{
		name: 'Algeria',
		countryCode: CountryCode.DZ,
		flagUrl: getFlagIconURLByCode(CountryCode.DZ, FlagIconSize.fourByThree),
	},
	{
		name: 'Angola',
		countryCode: CountryCode.AO,
		flagUrl: getFlagIconURLByCode(CountryCode.AO, FlagIconSize.fourByThree),
	},
	{
		name: 'Argentina',
		countryCode: CountryCode.AR,
		flagUrl: getFlagIconURLByCode(CountryCode.AR, FlagIconSize.fourByThree),
	},
	{
		name: 'Andorra',
		countryCode: CountryCode.AD,
		flagUrl: getFlagIconURLByCode(CountryCode.AD, FlagIconSize.fourByThree),
	},
	{
		name: 'Australia',
		countryCode: CountryCode.AU,
		flagUrl: getFlagIconURLByCode(CountryCode.AU, FlagIconSize.fourByThree),
	},
	{
		name: 'Austria',
		countryCode: CountryCode.AT,
		flagUrl: getFlagIconURLByCode(CountryCode.AT, FlagIconSize.fourByThree),
	},
	{
		name: 'Bahrain',
		countryCode: CountryCode.BH,
		flagUrl: getFlagIconURLByCode(CountryCode.BH, FlagIconSize.fourByThree),
	},
	{
		name: 'Bangladesh',
		countryCode: CountryCode.BD,
		flagUrl: getFlagIconURLByCode(CountryCode.BD, FlagIconSize.fourByThree),
	},
	{
		name: 'Belgium',
		countryCode: CountryCode.BE,
		flagUrl: getFlagIconURLByCode(CountryCode.BE, FlagIconSize.fourByThree),
	},
	{
		name: 'Benin',
		countryCode: CountryCode.BJ,
		flagUrl: getFlagIconURLByCode(CountryCode.BJ, FlagIconSize.fourByThree),
	},
	{
		name: 'Bhutan',
		countryCode: CountryCode.BT,
		flagUrl: getFlagIconURLByCode(CountryCode.BT, FlagIconSize.fourByThree),
	},
	{
		name: 'Bolivia',
		countryCode: CountryCode.BO,
		flagUrl: getFlagIconURLByCode(CountryCode.BO, FlagIconSize.fourByThree),
	},
	{
		name: 'Bosnia and Herzegovina',
		countryCode: CountryCode.BA,
		flagUrl: getFlagIconURLByCode(CountryCode.BA, FlagIconSize.fourByThree),
	},
	{
		name: 'Botswana',
		countryCode: CountryCode.BW,
		flagUrl: getFlagIconURLByCode(CountryCode.BW, FlagIconSize.fourByThree),
	},
	{
		name: 'Brazil',
		countryCode: CountryCode.BR,
		flagUrl: getFlagIconURLByCode(CountryCode.BR, FlagIconSize.fourByThree),
	},
	{
		name: 'Brunei',
		countryCode: CountryCode.BN,
		flagUrl: getFlagIconURLByCode(CountryCode.BN, FlagIconSize.fourByThree),
	},
	{
		name: 'Bulgaria',
		countryCode: CountryCode.BG,
		flagUrl: getFlagIconURLByCode(CountryCode.BG, FlagIconSize.fourByThree),
	},
	{
		name: 'Burkina Faso',
		countryCode: CountryCode.BF,
		flagUrl: getFlagIconURLByCode(CountryCode.BF, FlagIconSize.fourByThree),
	},
	{
		name: 'Cambodia',
		countryCode: CountryCode.KH,
		flagUrl: getFlagIconURLByCode(CountryCode.KH, FlagIconSize.fourByThree),
	},
	{
		name: 'Canada',
		countryCode: CountryCode.CA,
		flagUrl: getFlagIconURLByCode(CountryCode.CA, FlagIconSize.fourByThree),
	},
	{
		name: 'Cape Verde',
		countryCode: CountryCode.CV,
		flagUrl: getFlagIconURLByCode(CountryCode.CV, FlagIconSize.fourByThree),
	},
	{
		name: 'Chile',
		countryCode: CountryCode.CL,
		flagUrl: getFlagIconURLByCode(CountryCode.CL, FlagIconSize.fourByThree),
	},
	{
		name: 'Colombia',
		countryCode: CountryCode.CO,
		flagUrl: getFlagIconURLByCode(CountryCode.CO, FlagIconSize.fourByThree),
	},
	{
		name: 'Comoros',
		countryCode: CountryCode.KM,
		flagUrl: getFlagIconURLByCode(CountryCode.KM, FlagIconSize.fourByThree),
	},
	{
		name: 'Croatia',
		countryCode: CountryCode.HR,
		flagUrl: getFlagIconURLByCode(CountryCode.HR, FlagIconSize.fourByThree),
	},
	{
		name: 'Cyprus',
		countryCode: CountryCode.CY,
		flagUrl: getFlagIconURLByCode(CountryCode.CY, FlagIconSize.fourByThree),
	},
	{
		name: 'Czech Republic',
		countryCode: CountryCode.CZ,
		flagUrl: getFlagIconURLByCode(CountryCode.CZ, FlagIconSize.fourByThree),
	},
	{
		name: 'Denmark',
		countryCode: CountryCode.DK,
		flagUrl: getFlagIconURLByCode(CountryCode.DK, FlagIconSize.fourByThree),
	},
	{
		name: 'Djibouti',
		countryCode: CountryCode.DJ,
		flagUrl: getFlagIconURLByCode(CountryCode.DJ, FlagIconSize.fourByThree),
	},
	{
		name: 'Ecuador',
		countryCode: CountryCode.EC,
		flagUrl: getFlagIconURLByCode(CountryCode.EC, FlagIconSize.fourByThree),
	},
	{
		name: 'Egypt',
		countryCode: CountryCode.EG,
		flagUrl: getFlagIconURLByCode(CountryCode.EG, FlagIconSize.fourByThree),
	},
	{
		name: 'Estonia',
		countryCode: CountryCode.EE,
		flagUrl: getFlagIconURLByCode(CountryCode.EE, FlagIconSize.fourByThree),
	},
	{
		name: 'Eswatini',
		countryCode: CountryCode.SZ,
		flagUrl: getFlagIconURLByCode(CountryCode.SZ, FlagIconSize.fourByThree),
	},
	{
		name: 'Finland',
		countryCode: CountryCode.FI,
		flagUrl: getFlagIconURLByCode(CountryCode.FI, FlagIconSize.fourByThree),
	},
	{
		name: 'Fiji',
		countryCode: CountryCode.FJ,
		flagUrl: getFlagIconURLByCode(CountryCode.FJ, FlagIconSize.fourByThree),
	},
	{
		name: 'France',
		countryCode: CountryCode.FR,
		flagUrl: getFlagIconURLByCode(CountryCode.FR, FlagIconSize.fourByThree),
	},
	{
		name: 'Gabon',
		countryCode: CountryCode.GA,
		flagUrl: getFlagIconURLByCode(CountryCode.GA, FlagIconSize.fourByThree),
	},
	{
		name: 'Georgia',
		countryCode: CountryCode.GE,
		flagUrl: getFlagIconURLByCode(CountryCode.GE, FlagIconSize.fourByThree),
	},
	{
		name: 'Germany',
		countryCode: CountryCode.DE,
		flagUrl: getFlagIconURLByCode(CountryCode.DE, FlagIconSize.fourByThree),
	},
	{
		name: 'Ghana',
		countryCode: CountryCode.GH,
		flagUrl: getFlagIconURLByCode(CountryCode.GH, FlagIconSize.fourByThree),
	},
	{
		name: 'Greece',
		countryCode: CountryCode.GR,
		flagUrl: getFlagIconURLByCode(CountryCode.GR, FlagIconSize.fourByThree),
	},
	{
		name: 'Guinea',
		countryCode: CountryCode.GN,
		flagUrl: getFlagIconURLByCode(CountryCode.GN, FlagIconSize.fourByThree),
	},
	{
		name: 'Guyana',
		countryCode: CountryCode.GY,
		flagUrl: getFlagIconURLByCode(CountryCode.GY, FlagIconSize.fourByThree),
	},

	{
		name: 'Hong Kong',
		countryCode: CountryCode.HK,
		flagUrl: getFlagIconURLByCode(CountryCode.HK, FlagIconSize.fourByThree),
	},
	{
		name: 'Hungary',
		countryCode: CountryCode.HU,
		flagUrl: getFlagIconURLByCode(CountryCode.HU, FlagIconSize.fourByThree),
	},
	{
		name: 'Iceland',
		countryCode: CountryCode.IS,
		flagUrl: getFlagIconURLByCode(CountryCode.IS, FlagIconSize.fourByThree),
	},
	{
		name: 'India',
		countryCode: CountryCode.IN,
		flagUrl: getFlagIconURLByCode(CountryCode.IN, FlagIconSize.fourByThree),
	},
	{
		name: 'Indonesia',
		countryCode: CountryCode.ID,
		flagUrl: getFlagIconURLByCode(CountryCode.ID, FlagIconSize.fourByThree),
	},
	{
		name: 'Ireland',
		countryCode: CountryCode.IE,
		flagUrl: getFlagIconURLByCode(CountryCode.IE, FlagIconSize.fourByThree),
	},
	{
		name: 'Italy',
		countryCode: CountryCode.IT,
		flagUrl: getFlagIconURLByCode(CountryCode.IT, FlagIconSize.fourByThree),
	},
	{
		name: 'Ivory Coast (Côte d`Ivoire)',
		countryCode: CountryCode.CI,
		flagUrl: getFlagIconURLByCode(CountryCode.CI, FlagIconSize.fourByThree),
	},
	{
		name: 'Japan',
		countryCode: CountryCode.JP,
		flagUrl: getFlagIconURLByCode(CountryCode.JP, FlagIconSize.fourByThree),
	},

	{
		name: 'Jordan',
		countryCode: CountryCode.JO,
		flagUrl: getFlagIconURLByCode(CountryCode.JO, FlagIconSize.fourByThree),
	},
	{
		name: 'Kazakhstan',
		countryCode: CountryCode.KZ,
		flagUrl: getFlagIconURLByCode(CountryCode.KZ, FlagIconSize.fourByThree),
	},
	{
		name: 'Kenya',
		countryCode: CountryCode.KE,
		flagUrl: getFlagIconURLByCode(CountryCode.KE, FlagIconSize.fourByThree),
	},
	{
		name: 'Kiribati',
		countryCode: CountryCode.KI,
		flagUrl: getFlagIconURLByCode(CountryCode.KI, FlagIconSize.fourByThree),
	},
	{
		name: 'Kosovo',
		countryCode: CountryCode.XK,
		flagUrl: getFlagIconURLByCode(CountryCode.XK, FlagIconSize.fourByThree),
	},

	{
		name: 'Kuwait',
		countryCode: CountryCode.KW,
		flagUrl: getFlagIconURLByCode(CountryCode.KW, FlagIconSize.fourByThree),
	},
	{
		name: 'Kyrgyzstan',
		countryCode: CountryCode.KG,
		flagUrl: getFlagIconURLByCode(CountryCode.KG, FlagIconSize.fourByThree),
	},
	{
		name: 'Laos',
		countryCode: CountryCode.LA,
		flagUrl: getFlagIconURLByCode(CountryCode.LA, FlagIconSize.fourByThree),
	},
	{
		name: 'Latvia',
		countryCode: CountryCode.LV,
		flagUrl: getFlagIconURLByCode(CountryCode.LV, FlagIconSize.fourByThree),
	},
	{
		name: 'Lesotho',
		countryCode: CountryCode.LS,
		flagUrl: getFlagIconURLByCode(CountryCode.LS, FlagIconSize.fourByThree),
	},
	{
		name: 'Liberia',
		countryCode: CountryCode.LR,
		flagUrl: getFlagIconURLByCode(CountryCode.LR, FlagIconSize.fourByThree),
	},
	{
		name: 'Liechtenstein',
		countryCode: CountryCode.LI,
		flagUrl: getFlagIconURLByCode(CountryCode.LI, FlagIconSize.fourByThree),
	},
	{
		name: 'Lithuania',
		countryCode: CountryCode.LT,
		flagUrl: getFlagIconURLByCode(CountryCode.LT, FlagIconSize.fourByThree),
	},
	{
		name: 'Luxembourg',
		countryCode: CountryCode.LU,
		flagUrl: getFlagIconURLByCode(CountryCode.LU, FlagIconSize.fourByThree),
	},

	{
		name: 'Macau',
		countryCode: CountryCode.MO,
		flagUrl: getFlagIconURLByCode(CountryCode.MO, FlagIconSize.fourByThree),
	},
	{
		name: 'Madagascar',
		countryCode: CountryCode.MG,
		flagUrl: getFlagIconURLByCode(CountryCode.MG, FlagIconSize.fourByThree),
	},
	{
		name: 'Malawi',
		countryCode: CountryCode.MW,
		flagUrl: getFlagIconURLByCode(CountryCode.MW, FlagIconSize.fourByThree),
	},
	{
		name: 'Malaysia',
		countryCode: CountryCode.MY,
		flagUrl: getFlagIconURLByCode(CountryCode.MY, FlagIconSize.fourByThree),
	},
	{
		name: 'Maldives',
		countryCode: CountryCode.MV,
		flagUrl: getFlagIconURLByCode(CountryCode.MV, FlagIconSize.fourByThree),
	},
	{
		name: 'Malta',
		countryCode: CountryCode.MT,
		flagUrl: getFlagIconURLByCode(CountryCode.MT, FlagIconSize.fourByThree),
	},
	{
		name: 'Marshall Islands',
		countryCode: CountryCode.MH,
		flagUrl: getFlagIconURLByCode(CountryCode.MH, FlagIconSize.fourByThree),
	},
	{
		name: 'Mauritius',
		countryCode: CountryCode.MU,
		flagUrl: getFlagIconURLByCode(CountryCode.MU, FlagIconSize.fourByThree),
	},
	{ name: 'Mexico', countryCode: CountryCode.MX, flagUrl: getFlagIconURLByCode(CountryCode.MX, FlagIconSize.fourByThree) },
	{
		name: 'Micronesia',
		countryCode: CountryCode.FM,
		flagUrl: getFlagIconURLByCode(CountryCode.FM, FlagIconSize.fourByThree),
	},
	{
		name: 'Moldova',
		countryCode: CountryCode.MD,
		flagUrl: getFlagIconURLByCode(CountryCode.MD, FlagIconSize.fourByThree),
	},
	{
		name: 'Monaco',
		countryCode: CountryCode.MC,
		flagUrl: getFlagIconURLByCode(CountryCode.MC, FlagIconSize.fourByThree),
	},
	{
		name: 'Mongolia',
		countryCode: CountryCode.MN,
		flagUrl: getFlagIconURLByCode(CountryCode.MN, FlagIconSize.fourByThree),
	},
	{
		name: 'Montenegro',
		countryCode: CountryCode.ME,
		flagUrl: getFlagIconURLByCode(CountryCode.ME, FlagIconSize.fourByThree),
	},
	{
		name: 'Morocco',
		countryCode: CountryCode.MA,
		flagUrl: getFlagIconURLByCode(CountryCode.MA, FlagIconSize.fourByThree),
	},
	{
		name: 'Mozambique',
		countryCode: CountryCode.MZ,
		flagUrl: getFlagIconURLByCode(CountryCode.MZ, FlagIconSize.fourByThree),
	},
	{
		name: 'Namibia',
		countryCode: CountryCode.NA,
		flagUrl: getFlagIconURLByCode(CountryCode.NA, FlagIconSize.fourByThree),
	},
	{
		name: 'Nepal',
		countryCode: CountryCode.NP,
		flagUrl: getFlagIconURLByCode(CountryCode.NP, FlagIconSize.fourByThree),
	},
	{
		name: 'Netherlands',
		countryCode: CountryCode.NL,
		flagUrl: getFlagIconURLByCode(CountryCode.NL, FlagIconSize.fourByThree),
	},
	{
		name: 'New Zealand',
		countryCode: CountryCode.NZ,
		flagUrl: getFlagIconURLByCode(CountryCode.NZ, FlagIconSize.fourByThree),
	},
	{
		name: 'Niger',
		countryCode: CountryCode.NE,
		flagUrl: getFlagIconURLByCode(CountryCode.NE, FlagIconSize.fourByThree),
	},
	{
		name: 'Norway',
		countryCode: CountryCode.NO,
		flagUrl: getFlagIconURLByCode(CountryCode.NO, FlagIconSize.fourByThree),
	},

	{ name: 'North Macedonia', countryCode: CountryCode.MK, flagUrl: getFlagIconURLByCode(CountryCode.MK, FlagIconSize.fourByThree) },
	{
		name: 'Oman',
		countryCode: CountryCode.OM,
		flagUrl: getFlagIconURLByCode(CountryCode.OM, FlagIconSize.fourByThree),
	},
	{
		name: 'Palau',
		countryCode: CountryCode.PW,
		flagUrl: getFlagIconURLByCode(CountryCode.PW, FlagIconSize.fourByThree),
	},
	{
		name: 'Palestinian Territories',
		countryCode: CountryCode.PS,
		flagUrl: getFlagIconURLByCode(CountryCode.PS, FlagIconSize.fourByThree),
	},
	{
		name: 'Papua New Guinea',
		countryCode: CountryCode.PG,
		flagUrl: getFlagIconURLByCode(CountryCode.PG, FlagIconSize.fourByThree),
	},
	{ name: 'Paraguay', countryCode: CountryCode.PY, flagUrl: getFlagIconURLByCode(CountryCode.PY, FlagIconSize.fourByThree) },
	{ name: 'Peru', countryCode: CountryCode.PE, flagUrl: getFlagIconURLByCode(CountryCode.PE, FlagIconSize.fourByThree) },
	{
		name: 'Philippines',
		countryCode: CountryCode.PH,
		flagUrl: getFlagIconURLByCode(CountryCode.PH, FlagIconSize.fourByThree),
	},
	{
		name: 'Poland',
		countryCode: CountryCode.PL,
		flagUrl: getFlagIconURLByCode(CountryCode.PL, FlagIconSize.fourByThree),
	},
	{ name: 'Portugal', countryCode: CountryCode.PT, flagUrl: getFlagIconURLByCode(CountryCode.PT, FlagIconSize.fourByThree) },
	{
		name: 'Qatar',
		countryCode: CountryCode.QA,
		flagUrl: getFlagIconURLByCode(CountryCode.QA, FlagIconSize.fourByThree),
	},
	{ name: 'Romania', countryCode: CountryCode.RO, flagUrl: getFlagIconURLByCode(CountryCode.RO, FlagIconSize.fourByThree) },
	{
		name: 'Rwanda',
		countryCode: CountryCode.RW,
		flagUrl: getFlagIconURLByCode(CountryCode.RW, FlagIconSize.fourByThree),
	},
	{
		name: 'Samoa',
		countryCode: CountryCode.WS,
		flagUrl: getFlagIconURLByCode(CountryCode.WS, FlagIconSize.fourByThree),
	},
	{
		name: 'São Tomé and Príncipe',
		countryCode: CountryCode.ST,
		flagUrl: getFlagIconURLByCode(CountryCode.ST, FlagIconSize.fourByThree),
	},
	{
		name: 'Saudi Arabia',
		countryCode: CountryCode.SA,
		flagUrl: getFlagIconURLByCode(CountryCode.SA, FlagIconSize.fourByThree),
	},
	{ name: 'San Marino', countryCode: CountryCode.SM, flagUrl: getFlagIconURLByCode(CountryCode.SM, FlagIconSize.fourByThree) },
	{
		name: 'Senegal',
		countryCode: CountryCode.SN,
		flagUrl: getFlagIconURLByCode(CountryCode.SN, FlagIconSize.fourByThree),
	},
	{ name: 'Serbia', countryCode: CountryCode.RS, flagUrl: getFlagIconURLByCode(CountryCode.RS, FlagIconSize.fourByThree) },
	{
		name: 'Seychelles',
		countryCode: CountryCode.SC,
		flagUrl: getFlagIconURLByCode(CountryCode.SC, FlagIconSize.fourByThree),
	},
	{
		name: 'Singapore',
		countryCode: CountryCode.SG,
		flagUrl: getFlagIconURLByCode(CountryCode.SG, FlagIconSize.fourByThree),
	},
	{
		name: 'Sierra Leone',
		countryCode: CountryCode.SL,
		flagUrl: getFlagIconURLByCode(CountryCode.SL, FlagIconSize.fourByThree),
	},
	{ name: 'Slovakia', countryCode: CountryCode.SK, flagUrl: getFlagIconURLByCode(CountryCode.SK, FlagIconSize.fourByThree) },
	{ name: 'Slovenia', countryCode: CountryCode.SI, flagUrl: getFlagIconURLByCode(CountryCode.SI, FlagIconSize.fourByThree) },
	{
		name: 'Solomon Islands',
		countryCode: CountryCode.SB,
		flagUrl: getFlagIconURLByCode(CountryCode.SB, FlagIconSize.fourByThree),
	},
	{
		name: 'South Africa',
		countryCode: CountryCode.ZA,
		flagUrl: getFlagIconURLByCode(CountryCode.ZA, FlagIconSize.fourByThree),
	},
	{
		name: 'South Korea',
		countryCode: CountryCode.KR,
		flagUrl: getFlagIconURLByCode(CountryCode.KR, FlagIconSize.fourByThree),
	},
	{
		name: 'Spain',
		countryCode: CountryCode.ES,
		flagUrl: getFlagIconURLByCode(CountryCode.ES, FlagIconSize.fourByThree),
	},

	{
		name: 'Sri Lanka',
		countryCode: CountryCode.LK,
		flagUrl: getFlagIconURLByCode(CountryCode.LK, FlagIconSize.fourByThree),
	},
	{ name: 'Suriname', countryCode: CountryCode.SR, flagUrl: getFlagIconURLByCode(CountryCode.SR, FlagIconSize.fourByThree) },
	{
		name: 'Switzerland',
		countryCode: CountryCode.CH,
		flagUrl: getFlagIconURLByCode(CountryCode.CH, FlagIconSize.fourByThree),
	},
	{
		name: 'Sweden',
		countryCode: CountryCode.SE,
		flagUrl: getFlagIconURLByCode(CountryCode.SE, FlagIconSize.fourByThree),
	},
	{
		name: 'Taiwan',
		countryCode: CountryCode.TW,
		flagUrl: getFlagIconURLByCode(CountryCode.TW, FlagIconSize.fourByThree),
	},
	{
		name: 'Tanzania',
		countryCode: CountryCode.TZ,
		flagUrl: getFlagIconURLByCode(CountryCode.TZ, FlagIconSize.fourByThree),
	},
	{
		name: 'Thailand',
		countryCode: CountryCode.TH,
		flagUrl: getFlagIconURLByCode(CountryCode.TH, FlagIconSize.fourByThree),
	},
	{
		name: 'Timor-Leste',
		countryCode: CountryCode.TL,
		flagUrl: getFlagIconURLByCode(CountryCode.TL, FlagIconSize.fourByThree),
	},
	{
		name: 'Togo',
		countryCode: CountryCode.TG,
		flagUrl: getFlagIconURLByCode(CountryCode.TG, FlagIconSize.fourByThree),
	},
	{
		name: 'Tonga',
		countryCode: CountryCode.TO,
		flagUrl: getFlagIconURLByCode(CountryCode.TO, FlagIconSize.fourByThree),
	},
	{
		name: 'Tunisia',
		countryCode: CountryCode.TN,
		flagUrl: getFlagIconURLByCode(CountryCode.TN, FlagIconSize.fourByThree),
	},
	{ name: 'Türkiye', countryCode: CountryCode.TR, flagUrl: getFlagIconURLByCode(CountryCode.TR, FlagIconSize.fourByThree) },
	{
		name: 'Tuvalu',
		countryCode: CountryCode.TV,
		flagUrl: getFlagIconURLByCode(CountryCode.TV, FlagIconSize.fourByThree),
	},
	{
		name: 'Uganda',
		countryCode: CountryCode.UG,
		flagUrl: getFlagIconURLByCode(CountryCode.UG, FlagIconSize.fourByThree),
	},
	{
		name: 'United Arab Emirates',
		countryCode: CountryCode.AE,
		flagUrl: getFlagIconURLByCode(CountryCode.AE, FlagIconSize.fourByThree),
	},
	{
		name: 'United Kingdom',
		countryCode: CountryCode.GB,
		flagUrl: getFlagIconURLByCode(CountryCode.GB, FlagIconSize.fourByThree),
	},
	{
		name: 'United States',
		countryCode: CountryCode.US,
		flagUrl: getFlagIconURLByCode(CountryCode.US, FlagIconSize.fourByThree),
	},
	{
		name: 'Uruguay',
		countryCode: CountryCode.UY,
		flagUrl: getFlagIconURLByCode(CountryCode.UY, FlagIconSize.fourByThree),
	},
	{
		name: 'Uzbekistan',
		countryCode: CountryCode.UZ,
		flagUrl: getFlagIconURLByCode(CountryCode.LV, FlagIconSize.fourByThree),
	},
	{
		name: 'Vanuatu',
		countryCode: CountryCode.VU,
		flagUrl: getFlagIconURLByCode(CountryCode.LV, FlagIconSize.fourByThree),
	},
	{
		name: 'Vietnam',
		countryCode: CountryCode.VN,
		flagUrl: getFlagIconURLByCode(CountryCode.VN, FlagIconSize.fourByThree),
	},
	{
		name: 'Zambia',
		countryCode: CountryCode.ZM,
		flagUrl: getFlagIconURLByCode(CountryCode.ZM, FlagIconSize.fourByThree),
	},
];

export const MOCK_VALIDATION_BATCH_CSV_FILE_RESPONSE_COLUMN: ValidateBatchCsvFileResponseColumn = {
	fieldName: 'account.accountNumber',
	label: 'From Account Number',
};

export const MOCK_BATCH_PAYMENTS: BatchPayment[] = [
	{
		batchId: 'f2bf2990-a0dd-482b-9547-4f3dbbcaec6b',
		name: 'Mock Batch Payment',
		status: PaymentStatus.APPROVED,
		amount: 4000,
		source: PaymentSource.developerApi,
		developerInitiated: false,
		// workflowId: '51b812c7-31f9-48db-8e9b-0eecc6c70549',
		initiated: DateTime.now().toLocaleString(),
		valueDate: DateTime.now().plus({ days: 3 }).toLocaleString(),
		initiator: 'f2bf2990-a0dd-482b-9547-4f3dbbcaec6c',
		releaser: 'g2bf2990-a0dd-482b-9547-4f3dbbcaec6c',
		execution: {
			name: 'Workflow name',
			stages: [
				{
					stageId: 'string',
					name: 'Preparer',
					approval: {
						numApprovals: 0,
						userIds: ['string'],
						users: [
							{
								userId: 'string',
								email: 'string',
								familyName: 'string',
								givenName: 'string',
							},
						],
					},
					rules: [
						{
							condition: WorkflowStageRuleCondition.GREATER_THAN,
							numericConstant: 0,
							variable: WorkflowStageRuleVariable.PAYMENT_AMOUNT,
							approval: {
								numApprovals: 0,
								userIds: ['string'],
								users: [
									{
										userId: 'string',
										email: 'string',
										familyName: 'string',
										givenName: 'string',
									},
								],
							},
						},
					],
					status: PaymentStatus.APPROVED,
					signatures: [
						{
							userId: 'string',
							email: 'string',
							familyName: 'string',
							givenName: 'string',
							comment: '',
							timestamp: 'string',
						},
					],
					needsReview: false,
				},
			],
		},
		history: [
			{
				comment: 'string',
				description: 'string',
				metadata: {
					userId: 'string',
				},
				timestamp: DateTime.now().toLocaleString(),
			},
		],
		stats: {
			completedAmount: 10567,
			completedPayments: 30,
			openPayments: 5,
			rejectedPayments: 0,
			totalPayments: 30,
			openAmount: 1350,
		},
		submission: {
			completed: true,
			completedTimestamp: DateTime.now().toLocaleString(),
			submitted: true,
			submittedTimestamp: DateTime.now().toLocaleString(),
		},
		needsReview: false,
	},
];

export const MOCK_GET_PAYMENT_BATCHES_RESPONSE: GetBatchPaymentsResponse = {
	batches: MOCK_BATCH_PAYMENTS,
	totalCount: MOCK_BATCH_PAYMENTS.length,
};

export const MOCK_STAGE_FEEDBACK_FORM_VALUES: StageFeedbackFormValues = {
	stage: MOCK_PAYMENT_EXECUTION_STAGE,
	comment: 'test',
	feedbackType: StageFeedbackStatus.APPROVE,
};

export const MOCK_PAYMENT_HISTORY: PaymentHistory = {
	comment: 'test',
	description: 'test',
	metadata: { userId: 'test' },
	timestamp: '2021-06-01T19:05:20.250Z',
};

export const MOCK_PAYMENT_EXECUTION: PaymentExecution = {
	name: 'test',
	stages: [],
	workflowId: 'test',
};

export const MOCK_PAYMENT_INSTRUCTIONS_SOURCE: PaymentInstructionsSource = {
	name: 'template',
	type: 'Apple Template',
};

export const MOCK_PAYMENT_INSTRUCTIONS: PaymentInstructions = {
	source: MOCK_PAYMENT_INSTRUCTIONS_SOURCE,
	beneficiary: MOCK_BENEFICIARY,
	intermediaryBank: null,
	beneficiaryBank: MOCK_BENEFICIARY_BANK,
};

export const MOCK_PAYMENT_DETAIL_ACCOUNT: PaymentDetailAccount = {
	accountId: 'test',
	accountNumber: 'test',
	achCompanyId: 'test',
	address: MOCK_ADDRESS,
	companyName: 'test',
	institutionId: InstitutionId.BOFA,
	name: 'test',
	routingNumber: 'test',
	workflowId: 'test',
	currency: 'USD',
	country: 'US',
};

export const MOCK_PAYMENT_SUBMISSION: PaymentSubmission = {
	completed: false,
	completedTimestamp: null,
	confirmationId: '',
	endToEnd: '',
	institutionBatchId: '',
	institutionPaymentId: '',
	institutionSecondaryPaymentId: '',
	institutionStatus: '',
	submitted: false,
	submittedTimeStamp: null,
};

export const MOCK_PAYMENT: Payment = {
	account: MOCK_PAYMENT_DETAIL_ACCOUNT,
	amount: 1.0,
	confirmationId: '',
	developerInitiated: false,
	execution: MOCK_PAYMENT_EXECUTION,
	history: [MOCK_PAYMENT_HISTORY],
	initiated: DateTime.now().toISO(),
	initiator: 'f2bf2990-a0dd-482b-9547-4f3dbbcaec6b',
	institution: InstitutionId.JPM,
	source: PaymentSource.developerApi,
	instructions: MOCK_PAYMENT_INSTRUCTIONS,
	name: 'Mock Payment',
	paymentId: 'f2bf2990-a0dd-482b-9547-4f3dbbcaec6c',
	remittance: 'Remittance',
	status: PaymentStatus.APPROVED,
	submission: MOCK_PAYMENT_SUBMISSION,
	template: null,
	type: PaymentType.ACH,
	valueDate: DateTime.now().plus({ days: 3 }).toISO(),
	additionalInformation: {},
	needsReview: false,
};

export const MOCK_PAYMENTS: Payment[] = [MOCK_PAYMENT];

export const MOCK_VALIDATION_BATCH_CSV_FILE_RESPONSE: ValidateBatchCsvFileResponse = {
	columns: [MOCK_VALIDATION_BATCH_CSV_FILE_RESPONSE_COLUMN],
	payments: [],
};

export const MOCK_PAYMENT_STATS: PaymentStats = {
	status: {
		open: 0,
		approved: 0,
		submitted: 0,
		completed: 0,
		canceled: 0,
		rejected: 0,
		rejectedByBank: 0,
	},
	type: {
		rtp: 0,
		wire: 0,
		ach: 0,
		transfer: 0,
		withdrawal: 0,
	},
	breakdown: [
		{
			date: 'test',
			total: 0,
		},
	],
};

export const MOCK_PAYMENTS_SERVICE: {
	getPayments: () => Observable<Payment[]>;
	initiatePayment: (body: PaymentToCreate) => Observable<Payment>;
	setStageFeedback: (body: StageFeedback) => Observable<unknown>;
	submitPayment: (body: PaymentSubmissionOrCancelation) => Observable<unknown>;
	cancelPayment: (body: PaymentSubmissionOrCancelation) => Observable<unknown>;
} = {
	getPayments: () => of(MOCK_PAYMENTS),
	initiatePayment: () => of(MOCK_PAYMENT),
	setStageFeedback: () => of('test'),
	submitPayment: () => of(MOCK_PAYMENT),
	cancelPayment: () => of(MOCK_PAYMENT),
};

/**
 * Finds the execution stage that requires review for the current user.
 * Uses the back-end calculated `needsReview` flag on each stage.
 *
 * @param payment - The payment or batch payment to check
 * @returns The stage needing review, or undefined if none found
 */
export function getStageNeedingReview(payment: Payment | BatchPayment): PaymentExecutionStage | undefined {
	if (!payment.needsReview || !payment.execution?.stages) {
		return undefined;
	}
	return payment.execution.stages.find((stage: PaymentExecutionStage) => stage.needsReview === true);
}
